import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Link } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { useAppDispatch } from "app/redux-store-interactions";

import { bookmarksActions } from "./bookmarksSlice";

const LARGE_BOOKMARK_THRESHOLD = 1_000;
const BOOKMARK_MAX_SIZE = 15_000;

export function LargeBookmarkWarningDialog() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const resolveRef = useRef<((confirmed: boolean) => void) | null>(null);
    const [open, setOpen] = useState(false);
    const [count, setCount] = useState(0);

    const confirm = useCallback((objectCount: number) => {
        if (objectCount < LARGE_BOOKMARK_THRESHOLD) {
            return Promise.resolve(true);
        }

        return new Promise<boolean>((resolve) => {
            resolveRef.current = resolve;
            setCount(objectCount);
            setOpen(true);
        });
    }, []);

    useEffect(
        function initConfirmationFn() {
            dispatch(bookmarksActions.setConfirmLargeBookmark(confirm));
        },
        [dispatch, confirm],
    );

    const handleClose = (confirmed: boolean) => {
        setOpen(false);
        resolveRef.current?.(confirmed);
        resolveRef.current = null;
    };

    const threshold = count > BOOKMARK_MAX_SIZE ? BOOKMARK_MAX_SIZE : LARGE_BOOKMARK_THRESHOLD;
    const canCreate = count <= BOOKMARK_MAX_SIZE;

    return (
        <Dialog open={open} onClose={() => handleClose(false)}>
            <DialogTitle>{t("largeBookmarkWarningTitle", { objectCount: count.toLocaleString() })}</DialogTitle>
            <DialogContent>
                <DialogContentText>{t("largeBookmarkWarningBody", { threshold })}</DialogContentText>
                <DialogContentText component="div">
                    <ul style={{ paddingLeft: 20 }}>
                        <li>{t("largeBookmarkWarningTimeNote")}</li>
                    </ul>
                </DialogContentText>
                <DialogContentText>
                    {t("largeBookmarkWarningRecommendation")}
                    <br />
                    <Trans
                        i18nKey="largeBookmarkWarningBestPractice"
                        components={[
                            <Link
                                key="best-practice-link"
                                href="https://support.novorender.com/en/articles/395648-groups-and-bookmarks-best-practice"
                                target="_blank"
                                rel="noopener"
                            />,
                        ]}
                    />
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                {canCreate && (
                    <Button onClick={() => handleClose(true)} variant="outlined">
                        {t("continue")}
                    </Button>
                )}
                <Button variant="contained" onClick={() => handleClose(false)}>
                    {t("cancel")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
